import { SelectChangeEvent } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import { MaterialType, MaterialPropsType } from '../../api/appAPI'
import { EnqueueSnackbarType, showMessage } from '../common/Helpers'
import MaterialUIBox from './MaterialUIBox'

interface IProps {
  material: MaterialType
  textures: string[]
  distributorCompanies: string[]

  editMaterial(
    id: number,
    material: MaterialPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
}

const EditMaterial: FC<IProps> = ({ material, editMaterial, ...props }) => {
  const idRef = useRef(material.id)

  const [materialProps, setMaterialProps] = useState({
    name: material.name,
    displayName: material.displayName,
    sku: material.sku,
    type: material.type,
    cabinetDoor: material.cabinetDoor,
    thumbnailUrl: material.thumbnail,
    textureUrl: material.texture.url,
    isTexture: material.isTexture,
    length: material.length,
    width: material.width,
    description: material.description,
    weight: material.weight,
    price: material.price.toString(),
    addon: material.addon.toString(),
    permissions: material.permissions,
    distributorCompany: material.distributorCompany,
    threeProps: decodeURI(material.threeProps),
  })
  const materialPropsRef = useRef(materialProps)

  const [needSave, setNeedSave] = useState(false)
  const needSaveRef = useRef(needSave)

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    idRef.current = material.id

    setMaterialProps({
      name: material.name,
      displayName: material.displayName,
      sku: material.sku,
      type: material.type,
      cabinetDoor: material.cabinetDoor,
      thumbnailUrl: material.thumbnail,
      textureUrl: material.texture.url,
      isTexture: material.isTexture,
      length: material.length,
      width: material.width,
      description: material.description,
      weight: material.weight,
      price: material.price.toString(),
      addon: material.addon.toString(),
      permissions: material.permissions,
      distributorCompany: material.distributorCompany,
      threeProps: decodeURI(material.threeProps),
    })

    setNeedSave(false)
  }, [material])

  useEffect(() => {
    materialPropsRef.current = materialProps
    needSaveRef.current = needSave
  }, [needSave, materialProps])

  useEffect(
    () => () => {
      if (needSaveRef.current) {
        if (window?.confirm('Save material?')) {
          handleSaveClick()
        }
      }
    },
    [material]
  )

  const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
    setMaterialProps({
      ...materialProps,
      name: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const handleDisplayNameChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setMaterialProps({
      ...materialProps,
      displayName: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const handleSkuChange = (event: React.ChangeEvent<{ value: string }>) => {
    setMaterialProps({
      ...materialProps,
      sku: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setMaterialProps({
      ...materialProps,
      type: event.target.value,
    })
    setNeedSave(true)
  }

  const handleThumbnailUrlChange = (value: string) => {
    setMaterialProps({
      ...materialProps,
      thumbnailUrl: value,
    })
    setNeedSave(true)
  }
  const handleTextureUrlChange = (value: string) => {
    setMaterialProps({
      ...materialProps,
      textureUrl: value,
      thumbnailUrl: import.meta.env.VITE_TEXTURE_FOLDER_URL + value,
    })
    setNeedSave(true)
  }

  const handleIsTextureChange = (value: string) => {
    setMaterialProps({
      ...materialProps,
      isTexture: value,
    })
    setNeedSave(true)
  }

  const handleLengthChange = (value: number) => {
    setMaterialProps({
      ...materialProps,
      length: value,
    })
    setNeedSave(true)
  }

  const handleWidthChange = (value: number) => {
    setMaterialProps({
      ...materialProps,
      width: value,
    })
    setNeedSave(true)
  }

  const handleDescriptionChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setMaterialProps({
      ...materialProps,
      description: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const handleWeightChange = (value: number) => {
    setMaterialProps({
      ...materialProps,
      weight: value,
    })
    setNeedSave(true)
  }

  const handlePriceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setMaterialProps({
      ...materialProps,
      price: event.target.value as string,
    })
    setNeedSave(true)
  }

  const handleAddonChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setMaterialProps({
      ...materialProps,
      addon: event.target.value as string,
    })
    setNeedSave(true)
  }

  const handleDistributorCompanyChange = (event: SelectChangeEvent<string>) => {
    setMaterialProps({
      ...materialProps,
      distributorCompany: event.target.value,
    })
    setNeedSave(true)
  }

  const handlePermissionsChange = (value: string) => {
    setMaterialProps({
      ...materialProps,
      permissions: value,
    })
    setNeedSave(true)
  }

  const handleCabinetDoorChange = (value: string) => {
    setMaterialProps({
      ...materialProps,
      cabinetDoor: value,
    })
    setNeedSave(true)
  }

  const handleThreePropsChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    if (event.target.value.includes("'")) return

    setMaterialProps({
      ...materialProps,
      threeProps: event.target.value,
    })
    setNeedSave(true)
  }

  const handleSaveClick = () => {
    const name = materialPropsRef.current.name
    const displayName = materialPropsRef.current.displayName
    const sku = materialPropsRef.current.sku
    const type = materialPropsRef.current.type
    const cabinetDoor = materialPropsRef.current.cabinetDoor
    const thumbnailUrl = materialPropsRef.current.thumbnailUrl
    const textureUrl = materialPropsRef.current.textureUrl
    const isTexture = materialPropsRef.current.isTexture
    const length = materialPropsRef.current.length
    const width = materialPropsRef.current.width
    const description = materialPropsRef.current.description
    const weight = materialPropsRef.current.weight
    const price = materialPropsRef.current.price
    const addon = materialPropsRef.current.addon
    const permissions = materialPropsRef.current.permissions
    const distributorCompany = materialPropsRef.current.distributorCompany
    const threeProps = materialPropsRef.current.threeProps

    if (name === '') {
      showMessage(enqueueSnackbar, 'Enter material name')
      return
    }

    // if (sku === '') {
    //     showMessage(enqueueSnackbar, "Enter material sku")
    //     return
    // }

    if (type === '') {
      showMessage(enqueueSnackbar, 'Select material type')
      return
    }

    if (length === 0) {
      showMessage(enqueueSnackbar, 'Enter material texture length')
      return
    }

    if (width === 0) {
      showMessage(enqueueSnackbar, 'Enter material texture width')
      return
    }

    if (price === '') {
      showMessage(enqueueSnackbar, 'Enter material price')
      return
    }

    if (addon === '') {
      showMessage(enqueueSnackbar, 'Enter material price addon')
      return
    }

    needSaveRef.current = false
    setNeedSave(false)

    const materialProps = {
      // id,
      name,
      displayName,
      sku,
      type,
      cabinetDoor,
      thumbnail: thumbnailUrl,
      texture: textureUrl,
      isTexture,
      length,
      width,
      description,
      weight,
      price: parseFloat(price),
      addon: parseFloat(addon),
      permissions,
      editable: material.editable,
      distributorCompany,
      threeProps: encodeURI(threeProps),
    } as MaterialPropsType

    editMaterial(idRef.current, materialProps, enqueueSnackbar)
  }

  return (
    <MaterialUIBox
      title="Edit Material"
      name={{
        value: materialProps.name,
        onChange: handleNameChange,
      }}
      displayName={{
        value: materialProps.displayName,
        onChange: handleDisplayNameChange,
      }}
      sku={{
        value: materialProps.sku,
        onChange: handleSkuChange,
      }}
      type={{
        value: materialProps.type,
        onChange: handleTypeChange,
      }}
      cabinetDoor={{
        value: materialProps.cabinetDoor,
        set: handleCabinetDoorChange,
      }}
      // thumbnailUrl={{
      //     value: thumbnailUrl,
      //     onChange: handleThumbnailUrl
      // }}
      // textureUrl={{
      //     value: textureUrl,
      //     onChange: handleTextureUrl
      // }}
      isTexture={{
        value: materialProps.isTexture,
        set: handleIsTextureChange,
      }}
      thumbnailUrl={{
        value: materialProps.thumbnailUrl,
        onChange: handleThumbnailUrlChange,
      }}
      textureUrl={{
        value: materialProps.textureUrl,
        onChange: handleTextureUrlChange,
      }}
      length={{
        value: materialProps.length,
        onChange: handleLengthChange,
      }}
      width={{
        value: materialProps.width,
        onChange: handleWidthChange,
      }}
      description={{
        value: materialProps.description,
        onChange: handleDescriptionChange,
      }}
      weight={{
        value: materialProps.weight,
        onChange: handleWeightChange,
      }}
      price={{
        value: materialProps.price,
        onChange: handlePriceChange,
      }}
      addon={{
        value: materialProps.addon,
        onChange: handleAddonChange,
      }}
      permissions={{
        value: materialProps.permissions,
        set: handlePermissionsChange,
      }}
      distributorCompany={{
        value: materialProps.distributorCompany,
        onChange: handleDistributorCompanyChange,
      }}
      distributorCompanies={props.distributorCompanies}
      threeProps={{
        value: materialProps.threeProps,
        onChange: handleThreePropsChange,
      }}
      button={{
        text: 'Save',
        onClick: handleSaveClick,
      }}
      textures={props.textures}
      needSave={needSave}
      editable={material.editable === '1'}
    />
  )
}

export default EditMaterial
